import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminBackups.css';

function AdminBackups() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [creatingBackup, setCreatingBackup] = useState(null);

  const { data: backups, isLoading } = useQuery({
    queryKey: ['admin-backups'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/backups');
      return res.data;
    },
  });

  const createBackupMutation = useMutation({
    mutationFn: async (type) => {
      const res = await apiClient.post('/admin/backups', { type });
      return res.data;
    },
    onSuccess: (data, type) => {
      queryClient.invalidateQueries(['admin-backups']);
      setCreatingBackup(null);
      notification.success(`${type} backup'as pradėtas kuriamas fone`);
    },
    onError: (error) => {
      setCreatingBackup(null);
      notification.error(error.response?.data?.error || 'Nepavyko sukurti backup\'o');
    },
  });

  const handleCreateBackup = (type) => {
    setCreatingBackup(type);
    createBackupMutation.mutate(type);
  };

  const handleDownload = (backup) => {
    window.open(`/api/admin/backups/${backup.id}/download`, '_blank');
  };

  const deleteBackupMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`/admin/backups/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-backups']);
      notification.success('Backup\'as sėkmingai pašalintas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Nepavyko pašalinti backup\'o');
    },
  });

  const handleDelete = async (backup) => {
    const confirmed = await notification.showConfirm(
      `Ar tikrai norite pašalinti backup'ą "${backup.filename}"?`,
      'Patvirtinti backup pašalinimą'
    );
    if (confirmed) {
      deleteBackupMutation.mutate(backup.id);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('lt-LT');
  };

  if (isLoading) return <div className="admin-backups-page container">Kraunama...</div>;

  return (
    <div className="admin-backups-page container">
      <div className="backups-header">
        <h1>Atsarginės kopijos</h1>
        <div className="backup-actions">
          <button
            className="btn btn-secondary"
            onClick={() => handleCreateBackup('database')}
            disabled={creatingBackup !== null}
          >
            {creatingBackup === 'database' ? 'Kuriama...' : 'Duomenų bazės backup'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handleCreateBackup('files')}
            disabled={creatingBackup !== null}
          >
            {creatingBackup === 'files' ? 'Kuriama...' : 'Failų backup'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => handleCreateBackup('full')}
            disabled={creatingBackup !== null}
          >
            {creatingBackup === 'full' ? 'Kuriama...' : 'Pilnas backup'}
          </button>
        </div>
      </div>

      <div className="backups-list">
        {backups && backups.length > 0 ? (
          backups.map((backup) => (
            <div key={backup.id} className="backup-card">
              <div className="backup-info">
                <h3>{backup.filename}</h3>
                <div className="backup-meta">
                  <span className={`status-badge ${backup.status}`}>
                    {backup.status === 'completed' ? 'Baigtas' : backup.status === 'pending' ? 'Vykdomas' : 'Nepavyko'}
                  </span>
                  <span className="backup-type">{backup.type}</span>
                  <span className="backup-date">{formatDate(backup.created_at)}</span>
                  {backup.size > 0 && <span className="backup-size">{formatFileSize(backup.size)}</span>}
                </div>
                {backup.error_message && (
                  <p className="error-message">{backup.error_message}</p>
                )}
              </div>
              <div className="backup-actions">
                {backup.status === 'completed' && (
                  <button className="btn btn-primary" onClick={() => handleDownload(backup)}>
                    Atsisiųsti
                  </button>
                )}
                <button 
                  className="btn btn-danger" 
                  onClick={() => handleDelete(backup)}
                  disabled={deleteBackupMutation.isPending}
                >
                  {deleteBackupMutation.isPending ? 'Šalinama...' : 'Šalinti'}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>Backup'ų nėra. Sukurkite pirmąjį backup'ą.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminBackups;
